import { renderHubspotEmbeds } from "../inc/hubspot_forms.js";

// --- Module state (pan-to-search) ---
/** @type {AbortController | null} In-flight filter request, aborted when a newer one starts. */
let currentFilterController = null;
/** @type {boolean} Skip the next map `idle` so a programmatic fit doesn't trigger a pan re-query. */
let suppressNextIdle = false;

/**
 * Create a debounced wrapper that delays `fn` until `wait` ms after the last call.
 *
 * @param {(...args: any[]) => void} fn - Function to debounce.
 * @param {number} wait - Idle time in milliseconds before `fn` runs.
 * @returns {(...args: any[]) => void} The debounced function.
 */
function debounce(fn, wait) {
    let timer = null;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
    };
}

/**
 * Turn a `"%{from}-{to}"` range value into a sticky-bar button label. An empty
 * value (full span) reads as "Any"; a `to` at the ceiling gets a trailing "+".
 *
 * @param {string} rawValue - The hidden input's value, e.g. `"%200000-500000"`.
 * @param {any} hidden - The hidden range input (an ion.rangeSlider-augmented
 *   element), read for `data-oi-prefix` / `data-oi-postfix` / `data-oi-max`.
 * @param {(n: number) => string} [formatNumber] - Formats each bound; defaults to grouped digits.
 * @returns {string} The formatted label.
 */
function formatRangeValue(rawValue, hidden, formatNumber = (n) => n.toLocaleString()) {
    if (!rawValue) {
        return "Any";
    }
    const parts = rawValue.replace(/^%/, "").split("-");
    if (parts.length !== 2) {
        return "Any";
    }
    const from = parseInt(parts[0], 10);
    const to = parseInt(parts[1], 10);
    if (isNaN(from) || isNaN(to)) {
        return "Any";
    }
    const prefix = (hidden && hidden.dataset.oiPrefix) || "";
    const postfix = (hidden && hidden.dataset.oiPostfix) || "";
    const max = hidden && hidden.dataset.oiMax ? parseInt(hidden.dataset.oiMax, 10) : null;
    const fmt = (n) => prefix + formatNumber(n) + postfix;
    const toText = fmt(to) + (max !== null && to >= max ? "+" : "");
    return fmt(from) + " – " + toText;
}

/**
 * Link the sticky-bar range sliders to their modal twins. The modal form is the
 * single source of truth (it carries `oi-filter-change`), so a bar-slider change
 * is copied onto the modal control and re-fired there to run exactly one query;
 * a modal change mirrors back to the bar for display only.
 *
 * @returns {void}
 */
function setupRangeSliderLinks() {
    const modalForm = document.getElementById("oi-filter-form");
    if (!modalForm) {
        return;
    }
    const barForm = document.getElementById("oi-filter-form-partial");
    const RANGE_FIELDS = ["price", "square_footage"];
    let linking = false;

    // {from, to} for a "%from-to" value, defaulting to the slider's full span.
    const parseRange = (hidden) => {
        const min = hidden.dataset.oiMin ? parseInt(hidden.dataset.oiMin, 10) : 0;
        const max = hidden.dataset.oiMax ? parseInt(hidden.dataset.oiMax, 10) : 0;
        if (!hidden.value) {
            return { from: min, to: max };
        }
        const parts = hidden.value.replace(/^%/, "").split("-");
        return { from: parseInt(parts[0], 10) || min, to: parseInt(parts[1], 10) || max };
    };

    RANGE_FIELDS.forEach((field) => {
        const modalHidden = modalForm.querySelector('[name="' + field + '"]');
        const barHidden = barForm ? barForm.querySelector('[name="' + field + '"]') : null;
        const valueSpan = document.querySelector('[data-oi-bar-value="' + field + '"]');
        if (!modalHidden) {
            return;
        }

        const updateLabel = () => {
            if (valueSpan) {
                valueSpan.textContent = formatRangeValue(modalHidden.value, modalHidden);
            }
        };

        // Copy source's value onto target and move target's slider handles.
        // ion.rangeSlider.update() writes only to its own (nameless) text input,
        // so it never dispatches a change on the hidden input — no bounce.
        const mirror = (source, target) => {
            if (!target) {
                return;
            }
            target.value = source.value;
            if (target.oiSlider) {
                const range = parseRange(target);
                target.oiSlider.update({ from: range.from, to: range.to });
            }
        };

        if (barHidden) {
            barHidden.addEventListener("change", () => {
                if (linking) {
                    return;
                }
                linking = true;
                mirror(barHidden, modalHidden);
                updateLabel();
                modalHidden.dispatchEvent(new Event("change"));
                linking = false;
            });
        }

        modalHidden.addEventListener("change", () => {
            if (linking) {
                return;
            }
            linking = true;
            mirror(modalHidden, barHidden);
            updateLabel();
            linking = false;
        });

        updateLabel();
    });

    // ion.rangeSlider mis-measures inside a closed dropdown; recalc on open.
    if (barForm) {
        barForm.querySelectorAll(".filter-bar-dropdown").forEach((dropdown) => {
            dropdown.addEventListener("shown.bs.dropdown", () => {
                window.dispatchEvent(new Event("resize"));
            });
        });
    }
}

/**
 * Mirror the modal's single-select groups (bedrooms / bathrooms) back onto their
 * sticky-bar list dropdowns and keep the bar button label current. Bar -> modal
 * is already handled by the by-name sync in the template (it fires the modal's
 * `oi-filter-change`), so this only reflects the checked state and label outward.
 *
 * @returns {void}
 */
function setupListDropdownLinks() {
    const modalForm = document.getElementById("oi-filter-form");
    if (!modalForm) {
        return;
    }
    const barForm = document.getElementById("oi-filter-form-partial");
    const LIST_FIELDS = ["bedrooms", "bathrooms"];

    LIST_FIELDS.forEach((field) => {
        const modalRadios = modalForm.querySelectorAll('input[name="' + field + '"]');
        const barRadios = barForm ? barForm.querySelectorAll('input[name="' + field + '"]') : [];
        const valueSpan = document.querySelector('[data-oi-bar-value="' + field + '"]');
        if (!modalRadios.length || !barForm) {
            return;
        }

        // The bar row's own label carries the full text ("3+ Bedrooms" / "Any
        // bedrooms"), so the button reads exactly like the option the user chose.
        const updateLabel = () => {
            if (!valueSpan) {
                return;
            }
            const checked = barForm.querySelector('input[name="' + field + '"]:checked');
            if (!checked) {
                return;
            }
            const label = barForm.querySelector('label[for="' + checked.id + '"]');
            valueSpan.textContent = label ? label.textContent.trim() : checked.value.replace(/^@/, "");
        };

        modalRadios.forEach((radio) => {
            radio.addEventListener("change", () => {
                const checked = modalForm.querySelector('input[name="' + field + '"]:checked');
                const value = checked ? checked.value : "";
                barRadios.forEach((bar) => {
                    bar.checked = bar.value === value;
                });
                updateLabel();
            });
        });

        updateLabel();
    });
}

/**
 * Reset the matching sticky-bar select back to its placeholder. The bar → modal
 * sync is one-way, so a modal-side clear never reaches the bar's own `<select>`;
 * this closes that gap. No change dispatch — callers clear the modal (the source
 * of truth) first, which already fires the re-query.
 *
 * @param {string} name - The `name` attribute of the select to reset.
 * @returns {void}
 */
function syncBarSelectToPlaceholder(name) {
    const barForm = document.getElementById("oi-filter-form-partial");
    if (!name || !barForm) {
        return;
    }
    barForm.querySelectorAll("select").forEach((barSelect) => {
        if (barSelect.name === name) {
            barSelect.selectedIndex = 0;
        }
    });
}

// Range chips name their filter, and price reads compact ("$400K") so the chip stays short.
const COMPACT_NUMBER = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 });
const RANGE_BADGES = {
    price: { label: "Price Range", formatNumber: (n) => COMPACT_NUMBER.format(n) },
    square_footage: { label: "Square Feet" },
};

/**
 * Collect every active filter on the modal form (the single source of truth) as
 * `{label, clear}` entries. Each `clear` resets its own control the same way the
 * user would and dispatches change, so clearing runs one re-query and mirrors to
 * the sticky bar through the existing links.
 *
 * @param {any} modalForm - The modal filter form (queried loosely for its controls).
 * @returns {Array<{label: string, clear: () => void}>} One entry per active filter.
 */
function collectActiveFilters(modalForm) {
    const badges = [];

    // Range fields (price / square footage): value "%from-to".
    Object.entries(RANGE_BADGES).forEach(([field, { label, formatNumber }]) => {
        const hidden = modalForm.querySelector('input[name="' + field + '"]');
        if (!hidden || !hidden.value) {
            return;
        }
        badges.push({
            label: `${label}: ${formatRangeValue(hidden.value, hidden, formatNumber)}`,
            clear: () => {
                hidden.value = "";
                if (hidden.oiSlider) {
                    const min = parseInt(hidden.dataset.oiMin, 10) || 0;
                    const max = parseInt(hidden.dataset.oiMax, 10) || 0;
                    hidden.oiSlider.update({ from: min, to: max });
                }
                hidden.dispatchEvent(new Event("change"));
            },
        });
    });

    // Single-select radio groups (bedrooms / bathrooms): label reads "3+ Bedrooms".
    ["bedrooms", "bathrooms"].forEach((field) => {
        const checked = modalForm.querySelector('input[name="' + field + '"]:checked');
        if (!checked || !checked.value) {
            return;
        }
        // The modal pill shows just "4+"; append the noun so the chip reads
        // "4+ Bedrooms" on its own.
        const pill = modalForm.querySelector('label[for="' + checked.id + '"]');
        const pillText = pill ? pill.textContent.trim() : checked.value.replace(/^@/, "");
        const noun = field.charAt(0).toUpperCase() + field.slice(1);
        badges.push({
            label: pillText + " " + noun,
            clear: () => {
                const any = modalForm.querySelector('input[name="' + field + '"][value=""]');
                if (any) {
                    any.checked = true;
                    any.dispatchEvent(new Event("change", { bubbles: true }));
                }
            },
        });
    });

    // Multi-select checkboxes (home type / availability) + ff_master: one badge each.
    modalForm.querySelectorAll('input[type="checkbox"]:checked').forEach((checkbox) => {
        if (!checkbox.name) {
            return;
        }
        const label = modalForm.querySelector('label[for="' + checkbox.id + '"]');
        badges.push({
            label: label ? label.textContent.trim() : checkbox.value,
            clear: () => {
                checkbox.checked = false;
                checkbox.dispatchEvent(new Event("change", { bubbles: true }));
            },
        });
    });

    // Plain selects (city / floor plan / collection, etc.).
    modalForm.querySelectorAll("select").forEach((select) => {
        if (!select.value) {
            return;
        }
        const option = select.options[select.selectedIndex];
        badges.push({
            label: option ? option.textContent.trim() : select.value,
            clear: () => {
                select.selectedIndex = 0;
                select.dispatchEvent(new Event("change", { bubbles: true }));
                // The bar → modal mirror is one-way, so the matching sticky-bar select
                // keeps its old value after a badge ✕ or Reset. Snap it back to match; no
                // change dispatch — the modal clear above already fired the re-query.
                syncBarSelectToPlaceholder(select.name);
            },
        });
    });

    return badges;
}

/**
 * Rebuild the dismissable filter chips from the current modal state. Called on
 * load and after every re-query so the chips always track the applied filters.
 *
 * @returns {void}
 */
function renderFilterBadges() {
    const container = document.getElementById("filter-chips");
    const modalForm = document.getElementById("oi-filter-form");
    if (!container || !modalForm) {
        return;
    }
    const badgeClass = container.dataset.badgeClass || "";
    container.innerHTML = "";
    const activeFilters = collectActiveFilters(modalForm);
    activeFilters.forEach((filter) => {
        const badge = document.createElement("span");
        badge.className = "filter-chip" + (badgeClass ? " " + badgeClass : "");
        badge.append(filter.label + " ");
        const clear = document.createElement("button");
        clear.type = "button";
        clear.className = "clear-filter";
        clear.setAttribute("aria-label", "Clear filter");
        clear.innerHTML = '<i class="fa-light fa-x"></i>';
        clear.addEventListener("click", filter.clear);
        badge.appendChild(clear);
        container.appendChild(badge);
    });
    updateFilterCount(activeFilters.length);
}

/**
 * Update the "All Filters" button badge with the active-filter count, hiding it
 * when nothing is applied. Shares collectActiveFilters' tally with the chips.
 *
 * @param {number} count - Number of active filters.
 * @returns {void}
 */
function updateFilterCount(count) {
    const badge = document.getElementById("filter-active-count");
    if (!badge) {
        return;
    }
    badge.textContent = count ? String(count) : "";
    badge.classList.toggle("d-none", count === 0);
}

/**
 * Reset every active filter at once (modal footer "Reset Filters"). Reuses each
 * filter's own `clear()` — the same action the badge ✕ runs — so controls reset,
 * the bar mirrors, and badges refresh. The clears cascade through the shared
 * AbortController, so only the final (fully-cleared) re-query completes.
 *
 * @returns {void}
 */
function resetAllFilters() {
    const modalForm = document.getElementById("oi-filter-form");
    if (!modalForm) {
        return;
    }
    collectActiveFilters(modalForm).forEach((filter) => filter.clear());
}

/**
 * Get the oimap wrapper instance for the page's map (`oi.loaded.map["oi-map"]`).
 *
 * @returns {any} The oimap wrapper, or null if the map isn't loaded.
 */
function getMap() {
    return (oi.loaded && oi.loaded.map && oi.loaded.map["oi-map"]) || null;
}

/**
 * Get the underlying vendor map (Google/Mapbox native instance).
 *
 * @returns {any} The native map, or null if unavailable.
 */
function getNativeMap() {
    const wrap = getMap();
    return (wrap && wrap.vendor_implementation && wrap.vendor_implementation.map) || null;
}

/**
 * Whether the geomarket boundary has been removed (pan-to-search engaged).
 *
 * @returns {boolean} True when the `#oi-remove-boundary` input holds a truthy value.
 */
function isBoundaryRemoved() {
    const input = document.getElementById("oi-remove-boundary");
    return Boolean(input && input.value);
}

/**
 * Keep the count line's label in step with the boundary state: the geo-scoped
 * phrase ("Communities in {geomarket}") while the boundary is on, the generic
 * "results in this area" once pan-to-search drops it. No-op on pages without the
 * label span (Find Your Home, spec/lot lists carry no geo suffix).
 *
 * @returns {void}
 */
function updateCountLabel() {
    const label = document.getElementById("oi-filter-count-label");
    if (!label) {
        return;
    }
    const next = isBoundaryRemoved() ? label.dataset.areaLabel : label.dataset.scopedLabel;
    if (next != null) {
        label.textContent = next;
    }
}

/**
 * Read the map's current visible bounds into the form's hidden bounds inputs so
 * they ride along with every re-query (single path, per the validated contract).
 *
 * @returns {void}
 */
function writeMapBounds() {
    const nativeMap = getNativeMap();
    if (!nativeMap || typeof nativeMap.getBounds !== "function") {
        return;
    }
    const bounds = nativeMap.getBounds();
    if (!bounds) {
        return;
    }
    const ne = bounds.getNorthEast().toJSON();
    const sw = bounds.getSouthWest().toJSON();
    const fields = {
        "oi-bounds-ne-lat": ne.lat,
        "oi-bounds-ne-lng": ne.lng,
        "oi-bounds-sw-lat": sw.lat,
        "oi-bounds-sw-lng": sw.lng,
    };
    for (const id in fields) {
        const el = document.getElementById(id);
        if (el) {
            el.value = fields[id];
        }
    }
}

/**
 * Clear the hidden map-bounds inputs so the next re-query isn't clipped to a stale
 * viewport — used when the geomarket boundary is restored and the full region
 * should show again.
 *
 * @returns {void}
 */
function clearMapBounds() {
    ["oi-bounds-ne-lat", "oi-bounds-ne-lng", "oi-bounds-sw-lat", "oi-bounds-sw-lng"].forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
            /** @type {HTMLInputElement} */ (el).value = "";
        }
    });
}

/**
 * Set the remove-boundary state: update the hidden input and reflect it on the
 * toggle button (pressed state, active class, and on/off label text).
 *
 * @param {boolean} on - True to engage pan-to-search, false to restore the outline.
 * @returns {void}
 */
function setRemoveBoundary(on) {
    const input = document.getElementById("oi-remove-boundary");
    const btn = document.getElementById("oi-remove-boundary-toggle");
    if (input) {
        input.value = on ? "true" : "";
    }
    if (btn) {
        btn.setAttribute("aria-pressed", on ? "true" : "false");
        btn.classList.toggle("active", on);
        const label = btn.querySelector(".oi-remove-boundary-label");
        if (label) {
            label.textContent = on ? btn.dataset.labelOn : btn.dataset.labelOff;
        }
    }
}

/**
 * The geomarket outline, rendered as a Google polygon. Only present on city/county
 * geomarket pages (see common_map.html); null everywhere else.
 * @type {any}
 */
let geoBoundaryPolygon = null;

/**
 * Read and parse the geomarket boundary coordinates from the `#oi-geo-boundary`
 * script tag.
 *
 * @returns {Array<{lat: number, lng: number}> | null} The polygon path, or null
 *   when absent, empty, or unparseable.
 */
function getBoundaryCoords() {
    const el = document.getElementById("oi-geo-boundary");
    if (!el) {
        return null;
    }
    try {
        const coords = JSON.parse(el.textContent);
        return Array.isArray(coords) && coords.length ? coords : null;
    } catch {
        return null;
    }
}

/**
 * Draw the geomarket outline polygon onto the native map (once). No-op if it's
 * already drawn, the map/coords aren't ready, or Google isn't loaded.
 *
 * @returns {void}
 */
function drawBoundary() {
    if (geoBoundaryPolygon) {
        return;
    }
    const nativeMap = getNativeMap();
    const coords = getBoundaryCoords();
    if (!nativeMap || !coords || typeof google === "undefined") {
        return;
    }
    geoBoundaryPolygon = new google.maps.Polygon({
        paths: coords,
        // $primary-700, the site nav's navy
        strokeColor: "#002331",
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: "#000000",
        fillOpacity: 0,
        clickable: false,
    });
    geoBoundaryPolygon.setMap(nativeMap);
}

/**
 * Remove the geomarket outline polygon from the map, if present.
 *
 * @returns {void}
 */
function clearBoundary() {
    if (geoBoundaryPolygon) {
        geoBoundaryPolygon.setMap(null);
        geoBoundaryPolygon = null;
    }
}

/**
 * Build a LatLngBounds spanning the geomarket outline, or null when there's no
 * boundary (non-geomarket pages) or Google isn't loaded.
 *
 * @returns {any}
 */
function getBoundaryBounds() {
    const coords = getBoundaryCoords();
    if (!coords || typeof google === "undefined") {
        return null;
    }
    const bounds = new google.maps.LatLngBounds();
    // Multi-polygon outlines arrive as a list of rings
    coords.flat().forEach((point) => bounds.extend(point));
    return bounds;
}

/**
 * Frame the geomarket outline together with every result pin. A marker-only fit can
 * open a big district or county at street level on its one community, with the outline
 * off-screen; pins can also sit just outside the outline, since a community's geo comes
 * from its record, not its coordinates. No-op when there's no boundary (the marker-fit
 * owns the view). Suppresses the idle its programmatic fit fires so it isn't mistaken
 * for a user pan.
 *
 * @returns {void}
 */
function fitBoundaryAndMarkers() {
    const nativeMap = getNativeMap();
    if (!nativeMap) {
        return;
    }
    const bounds = getBoundaryBounds();
    if (!bounds) {
        return;
    }
    document.querySelectorAll(".oi-all-map-items .oi-map-stub").forEach((stub) => {
        const lat = parseFloat(stub.dataset.latitude);
        const lng = parseFloat(stub.dataset.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            bounds.extend({ lat, lng });
        }
    });
    suppressNextIdle = true;
    nativeMap.fitBounds(bounds);
}

/**
 * Push the response's new URL into browser history and update the canonical link,
 * so a filter/pan re-query is reflected in the address bar without a reload.
 *
 * @param {any} resp - Axios response; the new URL is read from `resp.data.data.url`.
 * @returns {void}
 */
function update_url(resp) {
    if (window.history && "pushState" in window.history) {
        const url = resp.data.data.url;
        window.history.pushState(null, "", url);
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical !== null) {
            canonical.href = url;
        }
    }
}

/**
 * Build the request payload from the filter form, collapsing repeated fields
 * (e.g. checkbox groups) into a single `|`-joined value per name — the shape the
 * server's `|`-splitting filter parser expects.
 *
 * @param {HTMLFormElement} filter_form - The filter form to serialize.
 * @returns {FormData} The collapsed form data.
 */
function getFormData(filter_form) {
    // Gather each field's value(s) in submission order, keyed by name.
    const valuesByName = new Map();
    new FormData(filter_form).forEach((value, name) => {
        const existing = valuesByName.get(name);
        if (existing) {
            existing.push(value);
        } else {
            valuesByName.set(name, [value]);
        }
    });

    const collapsed = new FormData();
    valuesByName.forEach((values, name) => {
        collapsed.append(name, values.join("|"));
    });
    return collapsed;
}

/**
 * Toggle the disabled state of every control in a form (no-op when the form is
 * absent). Buttons are form controls too, so this also locks the sticky bar's
 * dropdown toggles, not just its inputs.
 *
 * @param {HTMLFormElement|null} form - The form whose controls to toggle.
 * @param {boolean} disabled - True to disable, false to re-enable.
 * @returns {void}
 */
function setFormControlsDisabled(form, disabled) {
    if (!form) {
        return;
    }
    Array.from(form.elements).forEach((control) => {
        if (disabled) {
            control.setAttribute("disabled", "disabled");
        } else {
            /** @type {any} */ (control).disabled = false;
            control.removeAttribute("disabled");
        }
    });
}

/**
 * Disable the filter UI during a pending request: disable every control on both
 * the modal and sticky-bar forms plus any `.oi-disable-while-pending` element, and
 * grey out `.model-filters`. The bar mirrors changes into the modal, so it has to
 * lock too or the user can keep firing re-queries mid-flight.
 *
 * @param {HTMLFormElement} oi_filter_form - The modal filter form to disable.
 * @returns {void}
 */
function oiFilterDisableForm(oi_filter_form) {
    setFormControlsDisabled(oi_filter_form, true);
    setFormControlsDisabled(
        /** @type {HTMLFormElement|null} */ (document.getElementById("oi-filter-form-partial")),
        true,
    );
    document.querySelectorAll(".oi-disable-while-pending").forEach((el) => {
        el.setAttribute("disabled", "disabled");
    });
    // Grey the result panels to signal the list is refreshing.
    document.querySelectorAll(".model-filters").forEach((panel) => {
        /** @type {HTMLElement} */ (panel).style.opacity = "0.6";
    });
    // TODO: consider a full overlay + spinner instead of the opacity dim.
}

/**
 * Re-enable the filter UI after a request settles (on a short delay): clear the
 * disabled state from both the modal and sticky-bar forms and any
 * `.oi-disable-while-pending` element, and restore `.model-filters` opacity.
 *
 * @param {HTMLFormElement} oi_filter_form - The modal filter form to re-enable.
 * @returns {void}
 */
function oiFilterEnableForm(oi_filter_form) {
    setTimeout(function () {
        setFormControlsDisabled(oi_filter_form, false);
        setFormControlsDisabled(
            /** @type {HTMLFormElement|null} */ (document.getElementById("oi-filter-form-partial")),
            false,
        );
        document.querySelectorAll(".oi-disable-while-pending").forEach((el) => {
            /** @type {any} */ (el).disabled = false;
            el.removeAttribute("disabled");
        });
        document.querySelectorAll(".model-filters").forEach((panel) => {
            /** @type {HTMLElement} */ (panel).style.opacity = "1";
        });
    }, 500);
}

/**
 * Toggle the "pending" dim on the results list while a re-query is in flight
 * (Zillow-style): the current cards fade and stop taking clicks until the new
 * results paint. Used for fresh filter and pan re-queries, not load-more appends.
 *
 * @param {HTMLElement} results - The results container to dim or restore.
 * @param {boolean} pending - True to dim while loading, false to restore.
 * @returns {void}
 */
function setResultsPending(results, pending) {
    if (!results) {
        return;
    }
    results.style.transition = "opacity 0.15s ease";
    results.style.opacity = pending ? "0.5" : "1";
    results.style.pointerEvents = pending ? "none" : "";
}

/**
 * Pre-send hook: disable the form for a fresh query, but skip the disable/greying
 * for append (load-more) requests so scrolling stays responsive.
 *
 * @param {HTMLFormElement} oi_filter_form - The filter form.
 * @param {boolean} [from_infinite] - True when this is a load-more append request.
 * @returns {void}
 */
function oiFilterAjaxBeforesend(oi_filter_form, from_infinite) {
    if (from_infinite === undefined || from_infinite === false) {
        oiFilterDisableForm(oi_filter_form);
    }
}

/**
 * Watch the load-more sentinel; when it scrolls near view, bump the page input
 * from its `data-next-page` and fetch the next batch in append mode. One-shot per
 * sentinel — the response supplies a fresh one (or none, when the list is done).
 *
 * @param {any} oi_filter_form - The filter form (queried loosely for the page input).
 * @param {HTMLElement} results - The results container that holds the sentinel.
 * @param {any} sentinel - The current load-more sentinel element to observe.
 * @returns {void}
 */
function oiObserveSentinel(oi_filter_form, results, sentinel) {
    if (!sentinel || !("IntersectionObserver" in window)) {
        return;
    }
    const observer = new IntersectionObserver(
        function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) {
                    return;
                }
                const nextPage = sentinel.dataset.nextPage;
                if (!nextPage) {
                    return;
                }
                observer.unobserve(sentinel);
                const pageInput = oi_filter_form.querySelector('input[name="page"]');
                if (pageInput) {
                    pageInput.value = nextPage;
                }
                oiFilterGetResults(oi_filter_form, results, { append: true });
            });
        },
        // Prefetch the next batch two viewports before the sentinel scrolls into view, so
        // the cards are in place before the user reaches the end. Viewport-relative so
        // short batches (e.g. the 3-up floorplan grid) get the same runway on any screen.
        { rootMargin: "0px 0px 200% 0px" },
    );
    observer.observe(sentinel);
}

/**
 * Append a load-more batch: move the response's cards into the existing `.row` and
 * swap in the response's sentinel (absent when the list is exhausted).
 *
 * @param {any} oi_filter_form - The filter form (threaded to the sentinel observer).
 * @param {HTMLElement} results - The results container to append into.
 * @param {string} html - The rendered HTML for the next page of cards.
 * @returns {void}
 */
function appendResults(oi_filter_form, results, html) {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    const tmpRow = tmp.querySelector(".row");
    const existingRow = results.querySelector(".row");
    if (existingRow && tmpRow) {
        Array.from(tmpRow.children).forEach((card) => existingRow.appendChild(card));
    } else if (tmpRow) {
        results.insertAdjacentHTML("beforeend", html);
    }
    const oldSentinel = results.querySelector("#load-more-sentinel");
    if (oldSentinel) {
        oldSentinel.remove();
    }
    const newSentinel = tmp.querySelector("#load-more-sentinel");
    if (newSentinel) {
        results.appendChild(newSentinel);
        oiObserveSentinel(oi_filter_form, results, newSentinel);
    }
    announceResults(results, true);
}

/**
 * Let page scripts react to a freshly rendered or grown result list (e.g. the floorplan
 * CTA layout), without oifilter.js knowing about them.
 *
 * @param {HTMLElement} results - The results container that changed.
 * @param {boolean} append - True for a load-more batch, false for a fresh page 1.
 * @returns {void}
 */
function announceResults(results, append) {
    results.dispatchEvent(new CustomEvent("oifilter_results", { bubbles: true, detail: { append } }));
}

/**
 * Post the current filter state and render the results. Handles three modes: a
 * fresh filter query (resets to page 1, re-fits the map), a load-more append
 * (grows the visible list only), and a pan-driven re-query (keeps the user's
 * view). Cancels any in-flight request so rapid pans don't race or stack.
 *
 * @param {any} oi_filter_form - The filter form to submit (queried loosely for inputs).
 * @param {HTMLElement} results - The results container to render into.
 * @param {{fromPan?: boolean, append?: boolean}} [options] - `fromPan` skips the
 *   map re-fit; `append` grows the list without touching count/badges/URL/map.
 * @returns {void}
 */
function oiFilterGetResults(oi_filter_form, results, options) {
    options = options || {};
    const fromPan = options.fromPan === true;
    const append = options.append === true;
    // A fresh (non-append) query always starts at page 1 — clear any load-more cursor
    // left in the form so a filter change after scrolling doesn't request a deep page.
    if (!append) {
        const pageInput = oi_filter_form.querySelector('input[name="page"]');
        if (pageInput) {
            pageInput.value = "1";
        }
    }
    const ftype = oi_filter_form.dataset.filterType;
    const url = `/xhr/filter/${ftype}/`;
    // An in-flight query leaves the form disabled, and FormData skips disabled controls, so a
    // query fired mid-flight (e.g. reset clearing several filters) would post no page or filters
    // and get back every result unpaginated. Re-enable before reading; this query aborts that one.
    setFormControlsDisabled(oi_filter_form, false);
    const formData = getFormData(oi_filter_form);
    // Sort lives in #oi-sort-select, a list-header control outside the filter form,
    // so fold its value into every payload — filter change, load-more, and reset
    // alike — to keep the ordering stable as pages append.
    const sort_select = document.getElementById("oi-sort-select");
    if (sort_select && sort_select.value) {
        formData.append("sort", sort_select.value);
    }
    // Cancel any in-flight request so rapid pans don't race or stack.
    if (currentFilterController) {
        currentFilterController.abort();
    }
    currentFilterController = new AbortController();
    // Dim the current listings while the new results load. Skip load-more
    // appends so the growing list stays interactive as you scroll.
    if (!append) {
        setResultsPending(results, true);
    }
    oi.axios({
        method: "post",
        url: url,
        data: formData,
        signal: currentFilterController.signal,
        headers: {
            "Content-Type": "application/json",
        },
        transformRequest: [
            function (data, headers) {
                // We're not really transforming the request, but this function runs
                // before the ajax posts. Append (load-more) requests skip the form
                // disable/greying so scrolling stays responsive.
                oiFilterAjaxBeforesend(oi_filter_form, append);
                // We let the data pass-through unchanged.
                return data;
            },
        ],
    })
        .then(function (resp) {
            if (resp.data.data.result_list) {
                if (resp.data.options) {
                    const optionsById = resp.data.options;
                    for (const id in optionsById) {
                        document.getElementById(id).innerHTML = optionsById[id];
                    }
                }
                if (append) {
                    // Load-more: append the batch and re-arm the sentinel. Leave the
                    // count, badges, URL and map untouched — only the visible list grew.
                    appendResults(oi_filter_form, results, resp.data.data.result_list);
                    return;
                }
                results.innerHTML = resp.data.data.result_list;
                announceResults(results, false);
                // The inline Find Your Home form rides along with page 1 results.
                renderHubspotEmbeds(results).catch((error) => console.error(error));
                setResultsPending(results, false);
                // Arm the freshly-rendered sentinel (page cursor was reset pre-request).
                const sentinel = results.querySelector("#load-more-sentinel");
                if (sentinel) {
                    oiObserveSentinel(oi_filter_form, results, sentinel);
                }
                // A pan reset replaces the infinite-scrolled list with page 1, collapsing
                // the column height. If the user had scrolled down past the results, bring
                // the fresh list back into view so they aren't stranded in empty space.
                if (fromPan && results.getBoundingClientRect().top < 0) {
                    results.scrollIntoView({ behavior: "smooth", block: "start" });
                }
                const oi_filter_amount = document.getElementById("oi-filter-amount");
                // Check against null/undefined, not truthiness, so a 0-result
                // filter still renders "0" instead of keeping the stale count.
                if (oi_filter_amount !== null && resp.data.data.amount != null) {
                    oi_filter_amount.innerHTML = resp.data.data.amount;
                }
                updateCountLabel();
                renderFilterBadges();
                if (resp.data.result_list_lot) {
                    const results_lot = document.getElementById("oi-filter-results-lot");
                    results_lot.innerHTML = resp.data.result_list;
                }
                if (resp.data.result_list_land) {
                    const results_land = document.getElementById("oi-filter-results-land");
                    results_land.innerHTML = resp.data.result_list;
                }
                // Refresh the full-set marker container (payload lives under resp.data.data).
                // Update even when empty ("") so a zero-result filter clears the map.
                if (typeof resp.data.data.map_items === "string") {
                    const map_items = document.querySelector(".oi-all-map-items");
                    if (map_items) {
                        map_items.innerHTML = resp.data.data.map_items;
                    }
                }
                update_url(resp);
                if (window.afterResultsSuccess !== undefined) {
                    window.afterResultsSuccess();
                }
                const map_wrap = getMap();
                if (map_wrap) {
                    // Pan-driven re-query: keep the user's view (no re-fit). Filter-driven
                    // re-query: re-fit to the new result set, and suppress the idle that the
                    // programmatic fit fires so it doesn't bounce back into a pan re-query.
                    map_wrap.marker_autofit = fromPan ? "never" : "always";
                    if (!fromPan) {
                        suppressNextIdle = true;
                    }
                    // Re-plot from the map's own listing type, not the form's filter type.
                    // They match everywhere except model homes, which filter as "model" but
                    // carry "spec" markers (the spec_list_page branch in common_map.html) —
                    // passing "model" here finds no markers and wipes the map on every pan.
                    const mapEl = document.getElementById("oi-map");
                    const markerType = (mapEl && mapEl.dataset.listingType) || ftype;
                    map_wrap.set_markers(markerType);
                    if (!fromPan) {
                        map_wrap.fit();
                        // While the outline is on, keep it in view along with the new pins
                        if (!isBoundaryRemoved()) {
                            fitBoundaryAndMarkers();
                        }
                    }
                }
            }
        })
        .catch(function (error) {
            if (error.code === "ERR_CANCELED" || error.name === "CanceledError") {
                // Superseded by a newer request (rapid pan) — not an error. Leave the
                // dim in place; the superseding request owns restoring it.
                return;
            }
            setResultsPending(results, false);
            if (error.response) {
                if (error.response.status == 400) {
                    let msg = "";
                    const errors = error.response.data.errors;
                    for (let err in errors) {
                        let x = errors[err];
                        msg = `${x.code} - ${x.description}\n`;
                    }
                    oiFilterEnableForm(oi_filter_form);
                } else if (error.response.status == 302) {
                    // Redirecting
                    window.location.href = error.response.data.data.redirect;
                }
            } else if (error.request) {
                console.error(error.request);
            } else {
                console.error(error.message);
            }
        })
        .then(function () {
            const name_input = oi_filter_form.querySelector('input[name="m"]');
            if (name_input !== null) {
                name_input.value = "0";
            }
            oiFilterEnableForm(oi_filter_form);
        });
}
window.addEventListener("load", () => {
    const oi_filter_form = document.getElementById("oi-filter-form");
    setupRangeSliderLinks();
    setupListDropdownLinks();
    renderFilterBadges();

    let previous_highlighted_markers = [];

    /**
     * @param {any} map_wrap
     * @returns {void}
     */
    function unhighlight_markers(map_wrap) {
        while (previous_highlighted_markers.length) {
            map_wrap.vendor_implementation.unhighlight_marker(previous_highlighted_markers.pop());
        }
    }

    /**
     * @param {any} map_wrap
     * @param {string} listingId
     * @returns {void}
     */
    function highlight_markers(map_wrap, listingId) {
        unhighlight_markers(map_wrap);

        previous_highlighted_markers.push(listingId);

        map_wrap.vendor_implementation.highlight_marker(listingId);
    }

    // Hovering a pin highlights it like hovering its card does (card hovers open through
    // the marker's mouseover too), and it goes back once the infowindow closes.
    document.addEventListener("oimap_marker_mouseover", (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail || {};
        const map_wrap = getMap();
        if (detail.map_id !== "oi-map" || !detail.listing_id || !map_wrap) {
            return;
        }
        highlight_markers(map_wrap, String(detail.listing_id));
    });
    document.addEventListener("map_infowindow_close", () => {
        const map_wrap = getMap();
        if (map_wrap) {
            unhighlight_markers(map_wrap);
        }
    });

    const modal_reset_btn = document.getElementById("oi-modal-reset");
    if (modal_reset_btn) {
        modal_reset_btn.addEventListener("click", (e) => {
            e.preventDefault();
            resetAllFilters();
        });
    }
    if (oi_filter_form) {
        const oi_filter_clickables = oi_filter_form.querySelectorAll(".oi-filter-click");
        const oi_filter_changables = oi_filter_form.querySelectorAll(".oi-filter-change");
        oi_filter_clickables.forEach((elem) => {
            elem.addEventListener("click", (e) => {
                e.preventDefault();
                oiFilterGetResults(oi_filter_form, oi_filter_results);
            });
        });
        oi_filter_changables.forEach((elem) => {
            elem.addEventListener("change", (e) => {
                e.preventDefault();
                oiFilterGetResults(oi_filter_form, oi_filter_results);
            });
        });
        const oi_filter_reset = oi_filter_form.querySelectorAll(".oi-filter-reset");
        const oi_filter_results = document.getElementById("oi-filter-results");
        if (oi_filter_reset !== null && oi_filter_results !== null) {
            oi_filter_reset.forEach((btn) => {
                btn.addEventListener("click", (e) => {
                    let i;
                    e.preventDefault();
                    const to_clear = document.getElementsByClassName("oi-clear-on-reset");
                    if (to_clear) {
                        for (i = 0; i < to_clear.length; i++) {
                            if (to_clear[i].type == "select-one") {
                                to_clear[i].selectedIndex = 0;
                            }
                            if (to_clear[i].id == "boyl-input") {
                                to_clear[i].value = false;
                            }
                            if (to_clear[i].type == "checkbox") {
                                to_clear[i].checked = false;
                            }
                        }
                    }
                    const buttons_to_reset = document.getElementsByClassName("filter-button-on");
                    if (buttons_to_reset) {
                        for (i = 0; i < buttons_to_reset.length; i++) {
                            buttons_to_reset[i].classList.remove("filter-button-on");
                        }
                    }
                    oiFilterGetResults(oi_filter_form, oi_filter_results);
                });
            });
        }

        // The sort control sits outside the filter form, so wire it up directly:
        // a change re-queries from page 1 (getFormData folds in its value).
        const oi_sort_select = document.getElementById("oi-sort-select");
        if (oi_sort_select && oi_filter_results) {
            oi_sort_select.addEventListener("change", (e) => {
                e.preventDefault();
                oiFilterGetResults(oi_filter_form, oi_filter_results);
            });
        }

        // --- Scroll-driven load-more wiring ---
        if (oi_filter_results) {
            // Arm the server-rendered page-1 sentinel (if the list has more pages).
            const initial_sentinel = oi_filter_results.querySelector("#load-more-sentinel");
            if (initial_sentinel) {
                oiObserveSentinel(oi_filter_form, oi_filter_results, initial_sentinel);
            }
            // Markers live in the hidden .oi-all-map-items container, so the map's
            // built-in card-hover binding lands on the stubs, not these visible cards.
            // Re-bind here by listing id (delegated, so appended cards are covered too).
            let lastHoverId = null;
            oi_filter_results.addEventListener("mouseover", (e) => {
                const target = e.target;
                if (!(target instanceof Element)) {
                    return;
                }
                const card = target.closest(".oi-map-item");
                if (!card) {
                    lastHoverId = null;
                    return;
                }
                const listingId = card.getAttribute("data-listing-id");
                if (!listingId || listingId === lastHoverId) {
                    return;
                }
                lastHoverId = listingId;
                const map_wrap = /** @type {any} */ (getMap());
                if (map_wrap && typeof map_wrap.show_infowindow === "function") {
                    map_wrap.show_infowindow(listingId);

                    // HACK: These are not public functions and subject to change.
                    highlight_markers(map_wrap, listingId);
                }
            });
            // The infowindow closes when the pointer leaves (map_infowindow.js), so re-entering
            // the same card has to reopen it.
            oi_filter_results.addEventListener("mouseleave", () => {
                lastHoverId = null;
            });
        }

        // --- Pan-to-search wiring ---
        const remove_boundary_btn = document.getElementById("oi-remove-boundary-toggle");
        if (remove_boundary_btn) {
            remove_boundary_btn.addEventListener("click", (e) => {
                e.preventDefault();
                const turning_on = !isBoundaryRemoved();
                setRemoveBoundary(turning_on);
                if (turning_on) {
                    // Drop the outline and clip to the current view without moving the map.
                    clearBoundary();
                    writeMapBounds();
                    oiFilterGetResults(oi_filter_form, oi_filter_results, { fromPan: true });
                } else {
                    // Restore the outline and the full geomarket-scoped set: clear the map
                    // bounds so the server stops clipping to the last-panned view, then re-fit.
                    drawBoundary();
                    clearMapBounds();
                    oiFilterGetResults(oi_filter_form, oi_filter_results, { fromPan: false });
                }
            });
        }

        // The page can load already boundary-removed (a saved/refreshed pan-to-search
        // URL). The server renders #oi-remove-boundary accordingly; sync the toggle's
        // label and the count phrasing so the UI matches.
        if (isBoundaryRemoved()) {
            setRemoveBoundary(true);
            updateCountLabel();
        }

        // Every list map runs pan-to-search: a genuine pan/zoom clips the list, count,
        // and map to the visible bounds. On geomarket pages the geo scope stays on top
        // (results = region ∩ view) until the user clicks Remove Boundary; pages with no
        // outline (Find Your Home, market + spec/lot lists) engage it implicitly.
        const hasGeoBoundary = getBoundaryCoords() !== null;

        // Re-query only on a real user viewport change, never on a programmatic fitBounds
        // (the load-time marker-fit would otherwise drift a saved URL). A user drag, wheel
        // or pinch zoom, +/- button, or keyboard nudge always fires an input event on the
        // map first; a programmatic fit never does.
        let userMovedMap = false;
        const markUserMove = () => {
            userMovedMap = true;
        };

        const onMapIdle = debounce(() => {
            if (suppressNextIdle) {
                suppressNextIdle = false;
                return;
            }
            if (!userMovedMap) {
                return;
            }
            userMovedMap = false;
            // Outline-less pages have no toggle, so flag the drop to client-wide scope.
            if (!hasGeoBoundary) {
                setRemoveBoundary(true);
            }
            writeMapBounds();
            oiFilterGetResults(oi_filter_form, oi_filter_results, { fromPan: true });
        }, 350);

        let idle_bound = false;
        const bindMapIdle = () => {
            if (idle_bound) {
                return true;
            }
            const native_map = getNativeMap();
            if (!native_map || typeof google === "undefined") {
                return false;
            }
            google.maps.event.addListener(native_map, "idle", onMapIdle);
            // Capture phase so control buttons (zoom +/-) that stop propagation are still
            // seen. pointerdown covers drag, pinch, and button/marker taps; wheel covers
            // scroll zoom; keydown covers keyboard pan/zoom.
            const gesture_target = document.getElementById("oi-map");
            if (gesture_target) {
                gesture_target.addEventListener("pointerdown", markUserMove, { capture: true, passive: true });
                gesture_target.addEventListener("wheel", markUserMove, { capture: true, passive: true });
                gesture_target.addEventListener("keydown", markUserMove, { capture: true });
            }
            idle_bound = true;
            // Draw the geomarket outline once the map is live (unless already removed).
            if (!isBoundaryRemoved()) {
                drawBoundary();
                fitBoundaryAndMarkers();
            }
            return true;
        };

        // The native map may already be ready; otherwise bind once oimap settles.
        if (!bindMapIdle()) {
            const map_container = document.getElementById("oi-map");
            if (map_container) {
                map_container.addEventListener("oimap_idle", function on_ready() {
                    if (bindMapIdle()) {
                        map_container.removeEventListener("oimap_idle", on_ready);
                    }
                });
            }
        }
    }
    // This function OVERWRITES the default onpopstate behavior, which is perhaps desireable.
    // If overwriting is not necessary, the alternate function below is more desirable, as
    // it ADDS functiontionality.
    // window.onpopstate = function (event) {
    //     location.reload();
    // }
    let pathName = document.location.pathname;
    window.addEventListener("popstate", (e) => {
        if (pathName === e.target.location.pathname) {
            return false;
        }
        location.reload();
    });
});
